import React from 'react';
import { type StyleProp, StyleSheet, type ViewStyle } from 'react-native';
import { theme } from '@garnish/constants';

import type { TabItemId, TabItems } from '../../Tabs.types';
import { TabBarContainer } from '../TabBarContainer';
import { TabButton } from '../TabButton';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Renders the controlled "tablist" component along with the relevant "tab" components to change between tab panels.
 * Except that in this  case, the tabs are buttons, this wrapper just simplifies their usage.
 */
export const TabButtonBar = <Id extends TabItemId = TabItemId>(
  props: TabButtonBarProps<Id>,
) => {
  const { tabs, activeTabId, style, isFluid, onChange } = props;

  return (
    <TabBarContainer style={[styles.container, style]}>
      {tabs.map((tab) => {
        const { id, panelId, label, 'aria-label': ariaLabel, isDisabled } = tab;

        return (
          <TabButton<Id>
            key={id}
            id={id}
            label={label}
            panelId={panelId}
            isActive={activeTabId === id}
            ariaLabel={ariaLabel}
            isDisabled={isDisabled}
            isFluid={isFluid}
            onChange={onChange}
          />
        );
      })}
    </TabBarContainer>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    alignItems: 'center',
    justifyContent: 'space-evenly',
    gap: theme.spacing['4'],
    paddingBottom: theme.spacing['4'],
    borderBottomColor: theme.colors.DARK_KALE,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type TabButtonBarProps<Id extends TabItemId = TabItemId> = {
  tabs: TabItems<Id>;
  activeTabId: string;
  style?: StyleProp<ViewStyle>;
  isFluid?: boolean;
  onChange: (value: Id) => void;
};
